import {
  getRequestGeneric,
  postRequestGeneric,
  putRequestGeneric,
  deleteRequestGeneric,
} from "@/services/backendApiGeneric";

// ═══════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════

export type { Contact, ContactTypeInfo } from "./contactModel";
export { CONTACT_TYPES, RESPONSIBILITY_SUGGESTIONS, getContactTypeInfo, getContactDisplayName, emptyContact, contactRefusesKarsInvitations } from "./contactModel";
import type { Contact } from "./contactModel";

export interface Insurance {
  id: number;
  name: string;
  contactPerson: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  emailInvoice: string | null;
  streetNo: string | null;
  zip: string | null;
  city: string | null;
  country: string | null;
  notes: string | null;
  accountHolder: string | null;
  iban: string | null;
  bank: string | null;
  legalRepresentative: string | null;
  isClaimsRegulator: boolean;
  verified: boolean;
  source: string;
  createdByUser?: number | null;
  createdByOrg?: number | null;
  aiVerificationStatus?: string | null;
  aiVerificationNotes?: string | null;
  aiVerificationAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateInsurancePayload {
  name: string;
  country: string;
  website?: string;
  streetNo?: string;
  zip?: string;
  city?: string;
  email?: string;
  phone?: string;
  legalRepresentative?: string;
  isClaimsRegulator: boolean;
  notes?: string;
  /** If true, additionally file a GitHub issue so the KARS team can
   *  promote the entry into the global directory. */
  reportToKars: boolean;
}

export interface CreateInsuranceResult {
  insurance: Insurance;
  likelyDuplicate?: Insurance | null;
  issueNumber?: number | null;
  issueUrl?: string | null;
  issueError?: string | null;
  addedAsContact: boolean;
  contactId?: number | null;
  message: string;
}


// ═══════════════════════════════════════════════════════════════════
// Contacts API
// ═══════════════════════════════════════════════════════════════════

export function getContacts(): Promise<Contact[]> {
  return getRequestGeneric("/protected/contacts");
}

/** Kundenfeedback 02.11.2026 (I): Vermerk „Möchte keine KARS-Einladungen“ setzen/entfernen. */
export function setContactKarsInvitations(id: number, blocked: boolean): Promise<Contact> {
  return putRequestGeneric(`/protected/contacts/${id}/kars-invitations`, { blocked }, true);
}

// ═══════════════════════════════════════════════════════════════════
// Abbestellte Einladungen (Kundenfeedback 02.11.2026, I)
// ═══════════════════════════════════════════════════════════════════

export interface InvitationOptOut {
  idSenderOrganization: number;
  senderName: string;
  createdAt: string;
  createdBy?: string | null;
}

/** Absender-Organisationen, deren Einladungen die eigene Organisation nicht mehr annimmt. */
export function getInvitationOptOuts(): Promise<InvitationOptOut[]> {
  return getRequestGeneric("/protected/invitation-optouts");
}

/** Einladungen dieser Absender-Organisation wieder annehmen. */
export function deleteInvitationOptOut(idSenderOrganization: number): Promise<unknown> {
  return deleteRequestGeneric(`/protected/invitation-optouts/${idSenderOrganization}`);
}

// ═══════════════════════════════════════════════════════════════════
// Kunden aus den Fällen (Anmerkungen 25.07.26 VII)
// ═══════════════════════════════════════════════════════════════════

export interface CustomerCaseRef {
  idCase: number;
  caseNumber: string | null;
  caseDescription: string | null;
  status: string;
  openedAt: string | null;
}

export interface CustomerVehicle {
  idCase: number;
  licensePlate: string | null;
  manufacturer: string | null;
  model: string | null;
}

export interface CaseCustomer {
  name: string;
  clientType: string | null;
  email: string | null;
  mobile: string | null;
  landline: string | null;
  streetNo: string | null;
  zip: string | null;
  city: string | null;
  cases: CustomerCaseRef[];
  vehicles: CustomerVehicle[];
}

/** Kunden (Mandanten) aller Fälle der Organisation — durchsuch-/filterbar. */
export function getCaseCustomers(): Promise<CaseCustomer[]> {
  return getRequestGeneric("/protected/contacts/customers");
}

// ═══════════════════════════════════════════════════════════════════
// Live-Dublettenprüfung (Anlage-Dialoge Kunden & Kontakte)
// ═══════════════════════════════════════════════════════════════════

export interface DuplicateMatch {
  id?: number;
  source: "master" | "case" | "contact";
  name: string;
  city?: string;
  email?: string;
  phone?: string;
  reasons: string[];
  score: number;
}

export interface DuplicateCheckParams {
  type: "customer" | "contact";
  name?: string;
  company?: string;
  email?: string;
  phone?: string;
  excludeId?: number;
  responsibility?: string;
}

/** Antwort defensiv auspacken: SuccessJSON liefert je nach Endpunkt
 *  {success, data} oder den Body direkt (vgl. unwrapData in customersApi). */
export async function checkDuplicates(params: DuplicateCheckParams): Promise<DuplicateMatch[]> {
  const qs = new URLSearchParams();
  qs.set("type", params.type);
  if (params.name) qs.set("name", params.name);
  if (params.company) qs.set("company", params.company);
  if (params.email) qs.set("email", params.email);
  if (params.phone) qs.set("phone", params.phone);
  if (params.excludeId) qs.set("excludeId", String(params.excludeId));
  if (params.responsibility) qs.set("responsibility", params.responsibility);
  const res: unknown = await getRequestGeneric(
    `/protected/contacts/duplicate-check?${qs.toString()}`,
  );
  const data =
    res && typeof res === "object" && "data" in res ? (res as { data: unknown }).data : res;
  return Array.isArray(data) ? (data as DuplicateMatch[]) : [];
}

/** Akten-Beteiligung: Fall, in dem ein Adressbuch-Kontakt beteiligt ist. */
export interface ContactCaseRef {
  idCase: number;
  caseNumber: string | null;
  caseDescription: string | null;
  status: string;
  openedAt: string | null;
  role: string | null;
  source: "extern" | "portal";
}

function unwrap<T>(res: unknown, fallback: T): T {
  const data = res && typeof res === "object" && "data" in res ? (res as { data: unknown }).data : res;
  return (data ?? fallback) as T;
}

/** Anzahl der Fälle je Kontakt-ID (nur Kontakte mit mindestens einem Fall). */
export async function getContactCaseCounts(): Promise<Record<number, number>> {
  const res: unknown = await getRequestGeneric("/protected/contacts/case-counts");
  const raw = unwrap<Record<string, number>>(res, {});
  const out: Record<number, number> = {};
  for (const [k, v] of Object.entries(raw)) out[Number(k)] = Number(v) || 0;
  return out;
}

export async function getContactCases(id: number): Promise<ContactCaseRef[]> {
  const res: unknown = await getRequestGeneric(`/protected/contacts/${id}/cases`);
  const list = unwrap<unknown>(res, []);
  return Array.isArray(list) ? (list as ContactCaseRef[]) : [];
}

export function getContact(id: number): Promise<Contact> {
  return getRequestGeneric(`/protected/contacts/${id}`);
}

export function createContact(contact: Contact): Promise<Contact> {
  return postRequestGeneric("/protected/contacts", contact, true);
}

export function updateContact(id: number, contact: Contact): Promise<Contact> {
  return putRequestGeneric(`/protected/contacts/${id}`, contact, true);
}

export function deleteContact(id: number): Promise<void> {
  return deleteRequestGeneric(`/protected/contacts/${id}`);
}

export function toggleFavorite(id: number): Promise<Contact> {
  return postRequestGeneric(`/protected/contacts/${id}/toggle-favorite`, null, true);
}

// ═══════════════════════════════════════════════════════════════════
// Portal-Verknüpfung (Freundesliste)
// ═══════════════════════════════════════════════════════════════════

export interface CreateFromPortalPayload {
  idLinkedDepartment: number;
  contactType?: string | null;
  notes?: string | null;
}

/** Legt einen Kontakt als Portal-Verknüpfung auf Abteilungsebene an.
 *  Stammdaten werden ab sofort live aus dem Portal aufgelöst. */
export function createContactFromPortal(
  payload: CreateFromPortalPayload,
): Promise<Contact> {
  return postRequestGeneric("/protected/contacts/from-portal", payload, true);
}

/** Löst die Portal-Verknüpfung — bisheriger Stammdaten-Snapshot bleibt
 *  als manueller Kontakt erhalten (Recovery), wird in der UI aber ohne
 *  Portal-Badge angezeigt. */
export function unlinkContactFromPortal(id: number): Promise<Contact> {
  return deleteRequestGeneric(`/protected/contacts/${id}/portal-link`);
}

// ═══════════════════════════════════════════════════════════════════
// Insurances API (global directory, read-only)
// ═══════════════════════════════════════════════════════════════════

export function getInsurances(query?: string): Promise<Insurance[]> {
  const params = query ? `?q=${encodeURIComponent(query)}` : "";
  return getRequestGeneric(`/protected/insurances${params}`);
}

export function getInsurance(id: number): Promise<Insurance> {
  return getRequestGeneric(`/protected/insurances/${id}`);
}

/** Create a new insurance directory entry. The entry is stored immediately
 *  as an unverified draft (visible only to the submitting organization) and
 *  is additionally mirrored into the org's contacts list. If
 *  `payload.reportToKars` is true, a GitHub issue is filed for admin review. */
export function createInsurance(
  payload: CreateInsurancePayload,
): Promise<CreateInsuranceResult> {
  return postRequestGeneric("/protected/insurances", payload, true);
}
